import { KeyboardEvent, useCallback } from "react";

// Session F9 — the keyboard contract shared by every "pick one of these"
// control in the two v2 wizards.
//
// 🔑 A composite widget is ONE tab stop, not one per option. That is the
// difference between the ARIA radiogroup/tablist pattern and what a row of
// plain `<button>`s gives you for free: four ad-format tiles were four tab
// stops, and a keyboard user had to walk through every option of every group to
// reach the footer. Inside the group the ARROWS move, and — in both patterns
// this hook serves — selection FOLLOWS focus, so arrowing onto a tile chooses
// it exactly as clicking it would.
//
// The hook finds its items by a data attribute it stamps on them itself rather
// than by refs or by a role selector. Both wizards wrap their tiles in a
// `.amz-tiles__slot` span, and a disabled tile is wrapped again by an
// `OverlayTrigger` (a disabled button fires no pointer events, so its tooltip
// has to hang off the wrapper — B2's decision, still true). A ref array would
// have to be threaded through those wrappers; a `[role="radio"]` selector would
// break the day a tile is drawn as something else. The attribute survives both.
//
// It carries the item's LOGICAL index, so DOM order does not have to match the
// array's for `onActivate` to be right.

const ITEM_ATTR = "data-amz-roving";

export interface RovingItemProps {
  tabIndex: number;
  [ITEM_ATTR]: number;
}

interface UseRovingFocusArgs {
  /** How many options the group draws. */
  count: number;
  /** The chosen option, or -1 when nothing is chosen yet. */
  activeIndex: number;
  /** Choose the option at `index`. The arrows call this — see above. */
  onActivate: (index: number) => void;
  /**
   * Options the arrows must step OVER. A disabled tile is still drawn, still
   * read out and still carries its "Coming soon" badge; it just is not a place
   * the arrows can land, which is what `disabled` already means for the mouse.
   */
  isDisabled?: (index: number) => boolean;
  /**
   * `horizontal` is a tablist (ARIA's default for one). `both` is a radiogroup:
   * the tiles are a flex row that wraps, so which arrow "moves right" depends
   * on the viewport and all four have to work.
   */
  orientation?: "horizontal" | "vertical" | "both";
}

export interface RovingFocus {
  /** Spread onto the element that WRAPS the options. */
  containerProps: { onKeyDown: (event: KeyboardEvent<HTMLElement>) => void };
  /** Spread onto option `index` itself — the focusable element, not a wrapper. */
  itemProps: (index: number) => RovingItemProps;
}

const useRovingFocus = ({
  count,
  activeIndex,
  onActivate,
  isDisabled,
  orientation = "both",
}: UseRovingFocusArgs): RovingFocus => {
  // Which option holds the group's single tab stop. With nothing chosen yet the
  // ARIA pattern puts it on the first option you could actually choose, so that
  // tabbing in never lands on a dead tile.
  const firstEnabled = (() => {
    for (let index = 0; index < count; index += 1) {
      if (!isDisabled?.(index)) return index;
    }
    return 0;
  })();
  const stop =
    activeIndex >= 0 && activeIndex < count && !isDisabled?.(activeIndex)
      ? activeIndex
      : firstEnabled;

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const forward =
        (orientation !== "vertical" && event.key === "ArrowRight") ||
        (orientation !== "horizontal" && event.key === "ArrowDown");
      const back =
        (orientation !== "vertical" && event.key === "ArrowLeft") ||
        (orientation !== "horizontal" && event.key === "ArrowUp");
      const home = event.key === "Home";
      const end = event.key === "End";
      if (!forward && !back && !home && !end) return;

      const container = event.currentTarget;
      const items = Array.from(
        container.querySelectorAll<HTMLElement>(`[${ITEM_ATTR}]`)
      ).filter(
        (node) =>
          !node.hasAttribute("disabled") &&
          node.getAttribute("aria-disabled") !== "true"
      );
      if (items.length === 0) return;

      // Where we are is where the FOCUS is, not what is selected: an arrow
      // press only follows a previous arrow press or a tab-in, and both leave
      // focus on an item.
      const here = items.findIndex((node) => node === document.activeElement);
      let next: number;
      if (home) next = 0;
      else if (end) next = items.length - 1;
      else if (here === -1) next = 0;
      else
        next =
          (here + (forward ? 1 : -1) + items.length) % items.length;

      const node = items[next];
      if (!node) return;

      // The arrows own these keys inside a composite widget; left to the
      // browser they would scroll the step's column instead.
      event.preventDefault();
      node.focus();
      const logical = Number(node.getAttribute(ITEM_ATTR));
      if (!Number.isNaN(logical)) onActivate(logical);
    },
    [onActivate, orientation]
  );

  const itemProps = useCallback(
    (index: number): RovingItemProps => ({
      tabIndex: index === stop ? 0 : -1,
      [ITEM_ATTR]: index,
    }),
    [stop]
  );

  return { containerProps: { onKeyDown }, itemProps };
};

export default useRovingFocus;
